import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import Hint from 'components/styleguide/hint/Hint';
import Icon from 'components/styleguide/icon/Icon';
import IconButton from 'components/styleguide/icon_button/IconButton';

const styles = require('./DatePicker.module.scss');

const MONTHS_SHORT = moment.monthsShort();
const MONTHS_FULL = moment.months();
const WEEKDAYS_MIN = moment.weekdaysMin();
const POPOVER_WIDTH = 280;
const POPOVER_MARGIN = 16;
const DEFAULT_YEAR_LIMIT = 5;

export type DatePickerMode = 'date' | 'month-year';
export type DatePickerContext = 'neutral' | 'talent' | 'company';

export interface DateValue {
  year: number;
  month: number;
  day?: number;
}

export type DateLimit = DateValue | 'today';

type Step = 'year' | 'month' | 'day';

interface DatePickerProps {
  context?: DatePickerContext;
  defaultValue?: DateValue | null;
  disabled?: boolean;
  error?: string;
  inputId?: string;
  label?: string;
  maxDate?: DateLimit | null;
  minDate?: DateLimit | null;
  mode?: DatePickerMode;
  name?: string;
  onChange?: (value: DateValue | null) => void;
  placeholder?: string;
  required?: boolean;
  value?: DateValue | null;
  yearLimit?: number;
}

const compare = (a: DateValue, b: DateValue): number => {
  if (a.year !== b.year) return a.year - b.year;
  if (a.month !== b.month) return a.month - b.month;
  return (a.day ?? 0) - (b.day ?? 0);
};

const daysInMonth = (year: number, month: number): number =>
  new Date(year, month, 0).getDate();

const todayValue = (): DateValue => {
  const d = new Date();
  return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
};

const resolveLimit = (limit?: DateLimit | null): DateValue | null => {
  if (!limit) return null;
  if (limit === 'today') return todayValue();
  return limit;
};

const shiftYears = (v: DateValue, years: number): DateValue => ({
  ...v,
  year: v.year + years,
});

export default function DatePicker({
  context = 'neutral',
  defaultValue = null,
  disabled = false,
  error,
  inputId,
  label,
  maxDate,
  minDate,
  mode = 'month-year',
  name,
  onChange,
  placeholder = 'Select date',
  required = false,
  value,
  yearLimit = DEFAULT_YEAR_LIMIT,
}: DatePickerProps) {
  const includeDay = mode === 'date';
  const isControlled = value !== undefined;

  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<Step>('year');
  const [internalValue, setInternalValue] = useState<DateValue | null>(defaultValue);
  const currentValue = isControlled ? value : internalValue;
  const [tempYear, setTempYear] = useState<number | null>(currentValue?.year ?? null);
  const [tempMonth, setTempMonth] = useState<number | null>(currentValue?.month ?? null);
  const [alignRight, setAlignRight] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const selectedYearRef = useRef<HTMLButtonElement>(null);

  const commitValue = (next: DateValue | null) => {
    if (!isControlled) setInternalValue(next);
    onChange?.(next);
  };

  const iconColor = disabled ? 'neutral-border-disable' : 'neutral-border-darker';

  // Resolve bounds. Defaults to "past `yearLimit` years up to today" when
  // neither min nor max is given. When only one bound is given, the other is
  // inferred from yearLimit so the year list stays scoped.
  const lowerLimit = resolveLimit(minDate);
  const upperLimit = resolveLimit(maxDate);
  const effectiveMin: DateValue = lowerLimit
    ?? (upperLimit ? shiftYears(upperLimit, -yearLimit) : shiftYears(todayValue(), -yearLimit));
  const effectiveMax: DateValue = upperLimit
    ?? (lowerLimit ? shiftYears(lowerLimit, yearLimit) : todayValue());

  const years: number[] = [];
  for (let y = effectiveMax.year; y >= effectiveMin.year; y--) years.push(y);

  const displayValue = (() => {
    if (!currentValue) return '';
    if (includeDay && currentValue.day != null) {
      return `${currentValue.day} ${MONTHS_FULL[currentValue.month - 1]} ${currentValue.year}`;
    }
    return `${MONTHS_FULL[currentValue.month - 1]} ${currentValue.year}`;
  })();

  // ISO string for hidden-input form submission. Empty when no value selected.
  // `date` mode emits YYYY-MM-DD; `month-year` mode emits YYYY-MM.
  const inputValue = (() => {
    if (!currentValue) return '';
    const yyyy = String(currentValue.year).padStart(4, '0');
    const mm = String(currentValue.month).padStart(2, '0');
    if (includeDay && currentValue.day != null) {
      return `${yyyy}-${mm}-${String(currentValue.day).padStart(2, '0')}`;
    }
    return `${yyyy}-${mm}`;
  })();

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && step === 'year') selectedYearRef.current?.scrollIntoView({ block: 'center' });
  }, [isOpen, step]);

  // Auto-focus the selected (or first enabled) cell whenever a step opens, so
  // keyboard users can immediately drive the picker with arrow keys / Enter.
  useEffect(() => {
    if (!isOpen) return;
    const popover = popoverRef.current;
    if (!popover) return;

    const id = requestAnimationFrame(() => {
      const selected = popover.querySelector<HTMLButtonElement>('[data-selected="true"]');
      const fallback = popover.querySelector<HTMLButtonElement>('[data-grid] button:not(:disabled)');
      (selected ?? fallback)?.focus({ preventScroll: step === 'year' });
    });
    return () => cancelAnimationFrame(id);
  }, [isOpen, step]);

  const handlePopoverKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      triggerRef.current?.focus();
      return;
    }

    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;

    const grid = (e.target as HTMLElement).closest<HTMLElement>('[data-grid]');
    if (!grid) return;

    const buttons = Array.from(grid.querySelectorAll<HTMLButtonElement>('button:not(:disabled)'));
    const currentIndex = buttons.indexOf(document.activeElement as HTMLButtonElement);
    if (currentIndex < 0) return;

    const cols = Number(grid.dataset.cols) || 1;
    let nextIndex = currentIndex;
    if (e.key === 'ArrowRight') nextIndex = currentIndex + 1;
    if (e.key === 'ArrowLeft') nextIndex = currentIndex - 1;
    if (e.key === 'ArrowDown') nextIndex = currentIndex + cols;
    if (e.key === 'ArrowUp') nextIndex = currentIndex - cols;

    if (nextIndex < 0 || nextIndex >= buttons.length) return;
    e.preventDefault();
    buttons[nextIndex].focus();
  };

  const handleOpen = () => {
    setTempYear(currentValue?.year ?? null);
    setTempMonth(currentValue?.month ?? null);
    setStep('year');
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setAlignRight(rect.left + POPOVER_WIDTH > window.innerWidth - POPOVER_MARGIN);
    }
    setIsOpen(true);
  };

  const handleYearClick = (year: number) => {
    setTempYear(year);
    setStep('month');
  };

  const handleMonthClick = (month: number) => {
    if (tempYear == null) return;

    if (includeDay) {
      setTempMonth(month);
      setStep('day');
      return;
    }

    commitValue({ month, year: tempYear });
    setIsOpen(false);
  };

  const handleDayClick = (day: number) => {
    if (tempYear == null || tempMonth == null) return;
    commitValue({ day, month: tempMonth, year: tempYear });
    setIsOpen(false);
  };

  const handleBack = () => {
    if (step === 'day') setStep('month');
    else if (step === 'month') setStep('year');
  };

  const isYearDisabled = (year: number) => {
    if (year < effectiveMin.year) return true;
    if (year > effectiveMax.year) return true;
    return false;
  };

  const isMonthDisabled = (month: number) => {
    if (tempYear == null) return false;
    if (tempYear === effectiveMin.year && month < effectiveMin.month) return true;
    if (tempYear === effectiveMax.year && month > effectiveMax.month) return true;
    return false;
  };

  const isDayDisabled = (day: number) => {
    if (tempYear == null || tempMonth == null) return false;
    const candidate: DateValue = { year: tempYear, month: tempMonth, day };
    if (effectiveMin.day != null && compare(candidate, effectiveMin) < 0) return true;
    if (effectiveMax.day != null && compare(candidate, effectiveMax) > 0) return true;
    return false;
  };

  const renderDayGrid = () => {
    if (tempYear == null || tempMonth == null) return null;

    const totalDays = daysInMonth(tempYear, tempMonth);
    const firstWeekday = new Date(tempYear, tempMonth - 1, 1).getDay();
    const cells: Array<number | null> = [
      ...Array(firstWeekday).fill(null),
      ...Array.from({ length: totalDays }, (_, i) => i + 1),
    ];

    return (
      <div className={styles['day-panel']}>
        <div className={styles['weekday-row']}>
          {WEEKDAYS_MIN.map(w => (
            <span key={w} className={styles.weekday}>{w}</span>
          ))}
        </div>
        <div className={styles['day-grid']} data-grid data-cols="7">
          {cells.map((day, index) => {
            if (day == null) return <span key={`empty-${index}`} />;

            const isSelected = currentValue?.day === day && currentValue?.month === tempMonth && currentValue?.year === tempYear;
            return (
              <button
                key={day}
                type="button"
                className={classNames(styles['day-item'], { [styles['day-item-selected']]: isSelected })}
                onClick={() => handleDayClick(day)}
                disabled={isDayDisabled(day)}
                data-selected={isSelected ? 'true' : undefined}
              >
                {day}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const stepTitle = (() => {
    if (step === 'year') return 'Year';
    if (step === 'month') return 'Month';
    return tempYear != null && tempMonth != null
      ? `${MONTHS_FULL[tempMonth - 1]} ${tempYear}`
      : 'Day';
  })();

  return (
    <div ref={wrapperRef} className={classNames(styles.container, styles[context])}>
      {label && (
        <div className={styles['label-row']}>
          <label className={styles.label}>{label}</label>
          {required && <span className={styles.required} aria-hidden="true">*</span>}
        </div>
      )}

      <button
        ref={triggerRef}
        type="button"
        className={error ? styles['trigger-error'] : styles.trigger}
        onClick={handleOpen}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <Icon name="calendar" size="sm" color={iconColor} aria-hidden="true" />
        <span className={displayValue ? styles['trigger-text'] : styles['trigger-placeholder']}>
          {displayValue || placeholder}
        </span>
      </button>

      {name && <input type="hidden" id={inputId} name={name} value={inputValue} />}

      {error && <Hint variant="error" message={error} />}

      {isOpen && (
        <div
          ref={popoverRef}
          className={classNames(styles.popover, { [styles['popover-align-right']]: alignRight })}
          onKeyDown={handlePopoverKeyDown}
        >
          <div className={styles['popover-header']}>
            {step !== 'year' && (
              <div className={styles['header-back']}>
                <IconButton
                  aria-label="Back"
                  type="terceary"
                  iconName="arrowLeft"
                  size="S"
                  onClick={handleBack}
                />
              </div>
            )}
            <span className={styles['header-title']}>{stepTitle}</span>
          </div>

          {step === 'year' && (
            <div className={styles['grid-scrollable']} data-grid data-cols="3">
              {years.map((year) => {
                const isSelected = tempYear === year;
                return (
                  <button
                    key={year}
                    ref={isSelected ? selectedYearRef : undefined}
                    type="button"
                    className={classNames(styles['grid-item'], { [styles['grid-item-selected']]: isSelected })}
                    onClick={() => handleYearClick(year)}
                    disabled={isYearDisabled(year)}
                    data-selected={isSelected ? 'true' : undefined}
                  >
                    {year}
                  </button>
                );
              })}
            </div>
          )}

          {step === 'month' && (
            <div className={styles.grid} data-grid data-cols="3">
              {MONTHS_SHORT.map((m, i) => {
                const month = i + 1;
                const isSelected = tempMonth === month && tempYear != null;
                return (
                  <button
                    key={m}
                    type="button"
                    className={classNames(styles['grid-item'], { [styles['grid-item-selected']]: isSelected })}
                    onClick={() => handleMonthClick(month)}
                    disabled={isMonthDisabled(month)}
                    data-selected={isSelected ? 'true' : undefined}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
          )}

          {step === 'day' && renderDayGrid()}
        </div>
      )}
    </div>
  );
}
